import { DataModules, IDataState } from "@/store/modules/data/index";
import { ActionTree, GetterTree } from "vuex";
import { IState } from "@/store";
import { Methods } from "@/models/methods";
import { IUser } from "@/models/users";
import { ITicket } from "@/models/tickets";
import { ApiPathGetter } from "@/models/api";
import { IClient } from "@/models/clients";

const initialState = {} as IDataState;

const getters: GetterTree<IDataState, IState> = {
  apiPath: (
    s,
    g,
    rS,
    { isAdminContext, isMockClientContext }
  ): ApiPathGetter => () => {
    const admin = `api/admin/tickets`;
    const client = `api/tickets`;
    const contextual =
      isAdminContext && !isMockClientContext() ? admin : client;
    return { client, admin, contextual };
  },
  scope: () => ticketId => {
    return `$ticket_${ticketId}`;
  }
};

const actions: ActionTree<IDataState, IState> = {
  list: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/list",
      {
        ...payload,
        path: getters.apiPath().contextual,
        storeModule: DataModules.TICKETS
      },
      { root: true }
    );
  },
  get: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/get",
      {
        ...payload,
        path: `${getters.apiPath().contextual}/${payload.id}`,
        storeModule: DataModules.TICKETS
      },
      { root: true }
    );
  },
  create: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/create",
      {
        ...payload,
        path: getters.apiPath().contextual,
        storeModule: DataModules.TICKETS
      },
      { root: true }
    );
  },
  update: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/update",
      {
        ...payload,
        path: `${getters.apiPath().contextual}/${payload.id}`,
        storeModule: DataModules.TICKETS
      },
      { root: true }
    );
  },
  reply: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/callApi",
      {
        ...payload,
        method: Methods.POST,
        path: `${getters.apiPath().contextual}/${payload.id}/replies`,
        requestConfig: { data: payload.data }
      },
      { root: true }
    );
  },
  replyUpdate: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/callApi",
      {
        ...payload,
        method: Methods.PUT,
        path: `${getters.apiPath().contextual}/${payload.id}/replies/${
          payload.replyId
        }`,
        requestConfig: { data: payload.data }
      },
      { root: true }
    );
  },
  assignUsers: (
    { dispatch, getters },
    {
      ticketId,
      users
    }: {
      ticketId: ITicket["id"];
      users: { id: IUser["id"]; default?: boolean }[];
    }
  ) => {
    return dispatch(
      "data/callApi",
      {
        method: Methods.PUT,
        path: `${getters.apiPath().admin}/${ticketId}/users`,
        requestConfig: { data: { users } }
      },
      { root: true }
    );
  },
  changeStatus: ({ dispatch, getters }, payload) => {
    return dispatch(
      "data/callApi",
      {
        ...payload,
        method: Methods.PUT,
        path: `${getters.apiPath().contextual}/${payload.id}/status`,
        requestConfig: { data: { status_id: payload.statusId } }
      },
      { root: true }
    );
  },
  downloadEmailFile: ({ dispatch, getters }, payload) => {
    const { ticketId } = payload;
    return dispatch(
      "data/callApi",
      {
        method: Methods.GET,
        path: `${getters.apiPath().admin}/${ticketId}/download`,
        returnData: false,
        requestConfig: { responseType: "arraybuffer" }
      },
      { root: true }
    );
  },
  openNewTicketModal: async ({ rootGetters, dispatch }, props = {}) => {
    if (!rootGetters["user/can"]("create_ticket")) return;
    const openAddTicketModal = props =>
      dispatch(
        "ui/open/modal",
        {
          config: {
            component: () =>
              import("@/components/app/global/tickets/addTicketModal.vue"),
            props
          }
        },
        { root: true }
      );

    // If NO client, lead or account details passed, open select client modal
    if (!props.clientId && !props.accountId && !props.leadId) {
      const modal = await dispatch(
        "data/clients/openSelectClientModal",
        {
          events: {
            onSelect: (client: IClient) => {
              props.clientId = client.id;
              props.accountId = client.accounts?.[0]?.id;
              props.brandId = client.brand_id;
              openAddTicketModal(props);
              modal.close();
            }
          }
        },
        {
          root: true
        }
      );
      return;
    }
    return openAddTicketModal(props);
  },
  openPreferencesModal: ({ dispatch }, payload) => {
    dispatch(
      "ui/open/slideModal",
      {
        config: {
          component: () =>
            import("@/components/app/global/tickets/ticketSettingModal.vue"),
          ...payload
        }
      },
      { root: true }
    );
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  modules: {
    departments: require("./departments").default,
    files: require("./files").default,
    messages: require("./messages").default
  }
};
